import { StageDef } from '../automationMachineInterfaces'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  openClickableSettingModal,
  closeSettingModal,
  selectTargetSettinginDialog,
  getSettingModalSelectedOption,
} from './fbSettingsUtils'
import { howPeopleFindYouUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { FBRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import SectionNotFoundException from '../exception/sectionNotFoundException'
import {
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
  getCantUndoFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'
import {
  getLabelFromSurroundingText,
  getLabelsFromOption,
} from './fbSelectAudienceModalUtils'
import SettingNotFoundException from '../exception/settingNotFoundException'

// Setting property functions
const MESSAGE_REQUEST_VALUES = {
  CHATS: 'Chats',
  MESSAGE_REQUESTS: 'Message requests',
  DONT_RECEIVE_REQUESTS: "Don't receive requests",
}

const MESSAGE_REQUEST_SETTING_LEVELS = {
  [MESSAGE_REQUEST_VALUES.CHATS]: 0,
  [MESSAGE_REQUEST_VALUES.MESSAGE_REQUESTS]: 1,
  [MESSAGE_REQUEST_VALUES.DONT_RECEIVE_REQUESTS]: 2,
}

const TARGET_VALUES = {
  [FacebookSettingKey.PHONE_NUMBER_MESSAGE_REQUESTS]:
    MESSAGE_REQUEST_VALUES.MESSAGE_REQUESTS,
  [FacebookSettingKey.FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS]:
    MESSAGE_REQUEST_VALUES.MESSAGE_REQUESTS,
  [FacebookSettingKey.OTHER_MESSAGE_REQUESTS]:
    MESSAGE_REQUEST_VALUES.DONT_RECEIVE_REQUESTS,
}

const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value || null)
const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(
    key,
    (value) =>
      MESSAGE_REQUEST_SETTING_LEVELS[value] &&
      MESSAGE_REQUEST_SETTING_LEVELS[value] >=
        MESSAGE_REQUEST_SETTING_LEVELS[TARGET_VALUES[key]],
  )

const getCantUndoFn = (key) =>
  getCantUndoFnFromKey(
    key,
    (value) => MESSAGE_REQUEST_SETTING_LEVELS[value] === undefined,
  )

function getIsDisabledFn(settingsKey: string) {
  return (automationErrors) => {
    const error = automationErrors?.[settingsKey]?.gatherError
    return Boolean(error)
  }
}
function getIsDisabledReasonFn(settingsKey: string) {
  return (automationErrors) => {
    const error = automationErrors?.[settingsKey]?.gatherError
    if (error) {
      if (
        error.name === SettingNotFoundException.exceptionName ||
        error.name === SectionNotFoundException.exceptionName
      ) {
        return 'Your account does not have this setting'
      }
      return 'Unknown error'
    }
    return null
  }
}

// DOM Helpers
const PHONE_NUMBER_MESSAGE_REQUESTS_LABEL =
  'For people with your phone number, deliver requests to:'
const FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS_LABEL =
  'For friends of friends on Facebook, deliver requests to:'
const OTHER_MESSAGE_REQUESTS_LABEL =
  'For others on Messenger or Facebook, deliver requests to:'

export async function getCurrentSettingFromDialog(
  modalLabel: string,
): Promise<string | undefined> {
  const selectedElement = await getSettingModalSelectedOption(modalLabel)

  if (selectedElement.role === 'radio') {
    const labels = getLabelsFromOption(selectedElement)
    return labels[0]
  }

  // @type = "radio"
  return getLabelFromSurroundingText(selectedElement)
}

// Gather stage
enum StageKey {
  GATHER_SETTING_MESSAGE_REQUESTS = 'GATHER_SETTING_MESSAGE_REQUESTS',
  UPDATE_SETTING_MESSAGE_REQUESTS = 'UPDATE_SETTING_MESSAGE_REQUESTS',
  UNDO_SETTING_MESSAGE_REQUESTS = 'UNDO_SETTING_MESSAGE_REQUESTS',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_MESSAGE_REQUESTS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredAutomationErrors, updateStoredInitialValues } =
        userContext

      const automationErrors = {}

      let phoneNumberMsgRequests
      try {
        await openClickableSettingModal([PHONE_NUMBER_MESSAGE_REQUESTS_LABEL])
        phoneNumberMsgRequests = await getCurrentSettingFromDialog(
          PHONE_NUMBER_MESSAGE_REQUESTS_LABEL,
        )
        await closeSettingModal(PHONE_NUMBER_MESSAGE_REQUESTS_LABEL, 'CANCEL', {
          removeModalManually: true,
        })
      } catch (error) {
        automationErrors[FacebookSettingKey.PHONE_NUMBER_MESSAGE_REQUESTS] = {
          gatherError: { message: error.message, name: error.name },
        }
      }

      let friendsOfFriendsMsgRequests
      try {
        await openClickableSettingModal([
          FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS_LABEL,
        ])
        friendsOfFriendsMsgRequests = await getCurrentSettingFromDialog(
          FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS_LABEL,
        )
        await closeSettingModal(
          FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS_LABEL,
          'CANCEL',
          { removeModalManually: true },
        )
      } catch (error) {
        automationErrors[
          FacebookSettingKey.FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS
        ] = {
          gatherError: { message: error.message, name: error.name },
        }
      }

      let otherMsgRequests
      try {
        await openClickableSettingModal([OTHER_MESSAGE_REQUESTS_LABEL])
        otherMsgRequests = await getCurrentSettingFromDialog(
          OTHER_MESSAGE_REQUESTS_LABEL,
        )
        await closeSettingModal(OTHER_MESSAGE_REQUESTS_LABEL, 'CANCEL', {
          removeModalManually: true,
        })
      } catch (error) {
        automationErrors[FacebookSettingKey.OTHER_MESSAGE_REQUESTS] = {
          gatherError: { message: error.message, name: error.name },
        }
      }

      await updateStoredAutomationErrors(
        automationErrors,
        FBRecommendationKey.FB_MESSAGE_REQUESTS,
      )

      updateStoredInitialValues(
        {
          [FacebookSettingKey.PHONE_NUMBER_MESSAGE_REQUESTS]:
            phoneNumberMsgRequests,
          [FacebookSettingKey.FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS]:
            friendsOfFriendsMsgRequests,
          [FacebookSettingKey.OTHER_MESSAGE_REQUESTS]: otherMsgRequests,
        },
        FBRecommendationKey.FB_MESSAGE_REQUESTS,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_MESSAGE_REQUESTS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.PHONE_NUMBER_MESSAGE_REQUESTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([PHONE_NUMBER_MESSAGE_REQUESTS_LABEL])
        await selectTargetSettinginDialog(
          TARGET_VALUES[FacebookSettingKey.PHONE_NUMBER_MESSAGE_REQUESTS],
          isDry,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([
          FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS_LABEL,
        ])
        await selectTargetSettinginDialog(
          TARGET_VALUES[FacebookSettingKey.FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS],
          isDry,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.OTHER_MESSAGE_REQUESTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([OTHER_MESSAGE_REQUESTS_LABEL])
        await selectTargetSettinginDialog(
          TARGET_VALUES[FacebookSettingKey.OTHER_MESSAGE_REQUESTS],
          isDry,
        )
      }

      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_MESSAGE_REQUESTS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: howPeopleFindYouUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      const initialPhoneNumberMsgRequestsVal =
        initialValues[FacebookSettingKey.PHONE_NUMBER_MESSAGE_REQUESTS]
      const initialFriendsOfFriendsMsgRequestsVal =
        initialValues[FacebookSettingKey.FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS]
      const initialOtherMsgRequestsVal =
        initialValues[FacebookSettingKey.OTHER_MESSAGE_REQUESTS]

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.PHONE_NUMBER_MESSAGE_REQUESTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([PHONE_NUMBER_MESSAGE_REQUESTS_LABEL])
        await selectTargetSettinginDialog(
          initialPhoneNumberMsgRequestsVal,
          isDry,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([
          FRIENDS_OF_FRIENDS_MESSAGE_REQUESTS_LABEL,
        ])
        await selectTargetSettinginDialog(
          initialFriendsOfFriendsMsgRequestsVal,
          isDry,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.OTHER_MESSAGE_REQUESTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([OTHER_MESSAGE_REQUESTS_LABEL])
        await selectTargetSettinginDialog(initialOtherMsgRequestsVal, isDry)
      }

      return true
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
